import type { RequestFn } from '../http';

export interface HandoverUser {
  id: string;
  displayName: string;
  email?: string;
}

export interface ActiveHandover {
  id: string;
  source: 'CW' | 'WECLAPP';
  direction: 'PUSH' | 'PULL';
  /**
   * HANDOVER = Übergabe mit Rücksprache, der Empfänger bestätigt.
   * DISPATCH = Disposition durch Admin/Teamlead: sofort zugewiesen, ohne Bestätigung.
   */
  mode?: 'HANDOVER' | 'DISPATCH';
  cwTicketId: number | null;
  weclappTicketId: string | null;
  /** #T26-… aus dem Spiegel — für Anzeige und /t/-Deeplink (Liste /tickets/handovers). */
  weclappTicketNumber?: string | null;
  /** Aktueller Weclapp-Ticketstatus aus dem Spiegel (nur Liste /tickets/handovers). */
  ticketStatus?: string | null;
  /** Aktuell zuständiger Techniker laut Weclapp — kann nach der Übergabe abweichen. */
  ticketResponsibleUserName?: string | null;
  /** Ticket liegt im Portal-Papierkorb oder ist in Weclapp entfallen. */
  ticketDeleted?: boolean;
  ticketSummary: string | null;
  fromUser: HandoverUser;
  toUser: HandoverUser;
  reason: string;
  confirmedSpoken?: boolean;
  confirmedDocumented?: boolean;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELLED';
  responseNote?: string | null;
  respondedAt?: string | null;
  createdAt: string;
  isInitiator: boolean;
  isRecipient: boolean;
}

export interface PendingHandoverSummary {
  id: string;
  source: 'CW' | 'WECLAPP';
  direction?: 'PUSH' | 'PULL';
  cwTicketId: number | null;
  weclappTicketId: string | null;
  ticketSummary: string | null;
  fromUser: Pick<HandoverUser, 'id' | 'displayName'>;
  reason: string;
  createdAt: string;
}

export interface PendingClaimSummary {
  id: string;
  source: 'CW' | 'WECLAPP';
  cwTicketId: number | null;
  weclappTicketId: string | null;
  ticketSummary: string | null;
  claimerUser: Pick<HandoverUser, 'id' | 'displayName'>;
  reason: string;
  createdAt: string;
}

export interface PortalTechnician {
  id: string;
  displayName: string;
  isCurrentUser: boolean;
  cwMemberIdentifier?: string | null;
  weclappUserId?: string | null;
  /** Personenfarbe im ganzen ServiceHub (lib/personColors) */
  color?: string | null;
}

/**
 * Übergabe einleiten. Im Modus `HANDOVER` sind beide Bestätigungen Pflicht (Server prüft
 * das ebenfalls); im Modus `DISPATCH` — nur Admin/Teamlead — entfallen sie, dafür wird das
 * Ticket sofort zugewiesen.
 */
export type InitiateHandoverPayload = {
  toUserId: string;
  reason: string;
  ticketSummary?: string;
} & (
  | { mode?: 'HANDOVER'; confirmedSpoken: true; confirmedDocumented: true }
  | { mode: 'DISPATCH'; confirmedSpoken?: false; confirmedDocumented?: false }
);

export function createTicketHandoverApi(request: RequestFn) {
  return {
    /** CW: Übergabe einleiten (mode DISPATCH nur für Admin/Teamlead) */
    async initiateHandover(ticketId: number, data: InitiateHandoverPayload) {
      return request<{ handoverId: string; status: 'PENDING' | 'ACCEPTED' }>(
        `/tickets/${ticketId}/handover`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    /** Weclapp: Übergabe einleiten (mode DISPATCH nur für Admin/Teamlead) */
    async initiateWeclappHandover(ticketId: string, data: InitiateHandoverPayload) {
      return request<{ handoverId: string; status: 'PENDING' | 'ACCEPTED' }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/handover`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    /** CW: Aktive Übergabe für ein Ticket */
    async getActiveHandover(ticketId: number) {
      return request<{ handover: ActiveHandover | null }>(
        `/tickets/${ticketId}/handover/active`
      );
    },

    /** Weclapp: Aktive Übergabe für ein Ticket */
    async getWeclappActiveHandover(ticketId: string) {
      return request<{ handover: ActiveHandover | null }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/handover/active`
      );
    },

    /**
     * Übergaben-Übersicht. `scope: 'all'` wird nur für TEAMLEAD/ADMIN bedient — bei allen
     * anderen schränkt der Server stillschweigend auf die eigenen ein und meldet das über
     * das zurückgegebene `scope` zurück.
     */
    async getHandovers(params: { scope?: 'mine' | 'all'; status?: string; limit?: number } = {}) {
      const qs = new URLSearchParams();
      if (params.scope) qs.set('scope', params.scope);
      if (params.status) qs.set('status', params.status);
      if (params.limit) qs.set('limit', String(params.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<{
        handovers: ActiveHandover[];
        scope: 'mine' | 'all';
        maySeeAll: boolean;
      }>(`/tickets/handovers${suffix}`);
    },

    async getPendingHandoversForMe() {
      return request<{ handovers: PendingHandoverSummary[] }>(
        '/tickets/handovers/pending-for-me'
      );
    },

    async getHandoverById(handoverId: string) {
      return request<{ handover: ActiveHandover }>(
        `/tickets/handovers/${handoverId}`
      );
    },

    async respondToHandover(
      handoverId: string,
      data: { action: 'accept' | 'decline'; responseNote?: string }
    ) {
      return request<{ success: boolean; status: string }>(
        `/tickets/handovers/${handoverId}/respond`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async cancelHandover(handoverId: string) {
      return request<{ success: boolean; status: 'CANCELLED' }>(
        `/tickets/handovers/${handoverId}`,
        { method: 'DELETE' }
      );
    },

    /** Manueller Anstupser: erinnert den Empfänger sofort (Glocke + Push + Mail). */
    async nudgeHandover(handoverId: string) {
      return request<{ success: boolean }>(`/tickets/handovers/${handoverId}/nudge`, {
        method: 'POST',
      });
    },

    /** CW: Ticket-Übernahme anfragen (PULL) */
    async claimCwTicket(
      ticketId: number,
      data: {
        currentOwnerId: string;
        reason: string;
        confirmedSpoken: true;
        confirmedDocumented: true;
        ticketSummary?: string;
      }
    ) {
      return request<{ handoverId: string; status: 'PENDING'; direction: 'PULL' }>(
        `/tickets/${ticketId}/claim`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    /** Weclapp: Ticket-Übernahme anfragen (PULL) */
    async claimWeclappTicket(
      ticketId: string,
      data: {
        currentOwnerId: string;
        reason: string;
        confirmedSpoken: true;
        confirmedDocumented: true;
        ticketSummary?: string;
      }
    ) {
      return request<{ handoverId: string; status: 'PENDING'; direction: 'PULL' }>(
        `/weclapp/tickets/${encodeURIComponent(ticketId)}/claim`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    /** Ausstehende Übernahme-Anfragen auf meine Tickets */
    async getPendingClaimsOnMyTickets() {
      return request<{ claims: PendingClaimSummary[] }>(
        '/tickets/handovers/pending-claims-on-my-tickets'
      );
    },

    /** Alle aktiven Portal-Techniker (für Techniker-Auswahl in Team-Tickets) */
    async getPortalTechnicians() {
      return request<{ technicians: PortalTechnician[] }>('/portal/technicians');
    },
  };
}
